import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { UseMutationOptions, useQueryClient, useMutation } from 'react-query'
import { BrandVisualEnhancement, BrandVisualEnhancementResult, DeepPartial } from 'types'
import { cacheKeys } from './cacheKeys'
import { merge } from 'lodash'

interface Data {
  brand_visual_enhancement: BrandVisualEnhancement
}

type Variables = {
  input?: Record<string, any>
  result?: DeepPartial<BrandVisualEnhancementResult>
}

interface MutationContext {
  previousData?: Data
  queryKey: readonly unknown[]
}

/**
 * Build the request body. The Rails controller expects `input` and/or `result`
 * as *top‑level* keys (no `brand_visual_enhancement` wrapper).
 */
const buildPayload = ({
  input,
  result,
}: {
  input?: Record<string, any>
  result?: DeepPartial<BrandVisualEnhancementResult>
}) => {
  const payload: Record<string, any> = {}
  if (input && Object.keys(input).length) payload.input = input
  if (result && Object.keys(result).length) payload.result = result
  return payload
}

export const updateBrandVisualEnhancement = async ({
  workspaceId,
  brandVisualKitId,
  brandVisualEnhancementId,
  input,
  result,
  signal,
}: {
  workspaceId: number
  brandVisualKitId: number
  brandVisualEnhancementId: number
  input?: Record<string, any>
  result?: DeepPartial<BrandVisualEnhancementResult>
  signal?: AbortSignal
}) => {
  const res = await sendToApi<Data>(
    `/w/${workspaceId}/brand_visual_kits/${brandVisualKitId}/brand_visual_enhancements/${brandVisualEnhancementId}`,
    {
      method: 'PATCH',
      body: buildPayload({ input, result }),
      signal,
    },
  )

  if (res.type !== SUCCESS_RESPONSE_TYPE) throw new Error(res.data)

  return res.data
}

export const useUpdateBrandVisualEnhancement = (
  brandVisualKitId: number | undefined,
  brandVisualEnhancementId: number | undefined,
  options?: UseMutationOptions<Data, unknown, Variables, MutationContext>,
) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, unknown, Variables, MutationContext>(
    ({ input, result }) => {
      if (!brandVisualKitId) throw new Error('brandVisualKitId is required')
      if (!brandVisualEnhancementId) throw new Error('brandVisualEnhancementId is required')
      return updateBrandVisualEnhancement({ workspaceId, brandVisualKitId, brandVisualEnhancementId, input, result })
    },
    {
      ...options,

      // Optimistic update: merge new edits into cache so nothing flashes away.
      onMutate: async ({ input, result }): Promise<MutationContext | undefined> => {
        if (!brandVisualKitId || !brandVisualEnhancementId) return

        const queryKey = cacheKeys.detail(workspaceId, brandVisualKitId, brandVisualEnhancementId)

        await queryClient.cancelQueries({ queryKey })

        const previousData = queryClient.getQueryData<Data>(queryKey)
        if (!previousData) return { previousData, queryKey }

        const optimisticData: Data = merge({}, previousData, {
          brand_visual_enhancement: {
            input,
            result,
          },
        })

        queryClient.setQueryData<Data>(queryKey, optimisticData)

        return { previousData, queryKey }
      },

      // Roll back if the request fails.
      onError: (error, variables, context) => {
        if (context?.previousData && context?.queryKey) {
          queryClient.setQueryData(context.queryKey, context.previousData)
        }
        options?.onError?.(error, variables, context)
      },

      // Always refresh final data.
      onSettled: (data, error, variables, context) => {
        if (context?.queryKey) {
          queryClient.invalidateQueries({ queryKey: context.queryKey })
        }
        options?.onSettled?.(data, error, variables, context)
      },
    },
  )
}
